import { inject, Injectable } from '@angular/core';
import {
  ClientQueryParams,
  createQueryParams,
  IAssociateClientRequest,
  IAssociateClientResponse,
  IBeneficiary,
  IBeneficiaryRequest,
  IClient,
  IClientDocument,
  IClientDocumentUploadRequest,
  IClientsRequest,
  IClientsStats,
  IClientStatement,
  IClientStatementQueryParams,
  IClientStateQueryParams,
  IClientStats,
  ICorporateClient,
  ICorporateClientUpdateRequest,
  ICorporateRequest,
  IIndividualClient,
  ILookupRequest,
  ILookupResponse,
  IndividualClientUpdateRequest,
  IPaginatedResponse,
  ISuccessulResponse,
  ITransactionStats,
  IVehicle,
  IVehicleUpdateRequest,
  SystemUserRole,
} from '@inshuwa-client/models';
import { BehaviorSubject, map, Observable, switchMap, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { FilesService } from './files.service';
import { NgHttpCachingHeaders, NgHttpCachingService } from 'ng-http-caching';
import { NzModalService } from 'ng-zorro-antd/modal';
import { GetDecodedToken, GetUserPermission } from '../utils/token-decode';
import { baseUrl } from '../configs/baseUrl';
import { SharedAuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class ClientsService {
  readonly uploadService = inject(FilesService);
  readonly http = inject(HttpClient);
  readonly cachingService = inject(NgHttpCachingService);
  readonly nzModalService = inject(NzModalService);

  isLoggedIn$ = new BehaviorSubject(false);
  listCacheTag = 'client-listing-tag';

  getClientsStats(
    params?: Partial<IClientStateQueryParams>,
  ): Observable<IClientsStats> {
    return this.http.get<IClientsStats>(`${baseUrl()}/clients/stats`, {
      params: createQueryParams(params),
      headers: {
        [NgHttpCachingHeaders.TAG]: this.listCacheTag,
      },
    });
  }

  getClientsTransactionStats(
    params?: Partial<IClientStateQueryParams>,
  ): Observable<ITransactionStats> {
    return this.http.get<ITransactionStats>(
      `${baseUrl()}/clients/stats/transactions`,
      {
        params: createQueryParams(params),
        headers: {
          [NgHttpCachingHeaders.TAG]: this.listCacheTag,
        },
      },
    );
  }

  getAllClients(
    query?: Partial<ClientQueryParams>,
  ): Observable<IPaginatedResponse<IClient>> {
    return this.http.get<IPaginatedResponse<IClient>>(`${baseUrl()}/clients`, {
      params: createQueryParams(query),
      headers: {
        [NgHttpCachingHeaders.TAG]: this.listCacheTag,
      },
    });
  }

  public createCorporateClient(
    client: Partial<ICorporateRequest>,
  ): Observable<IClient> {
    return this.http
      .post<IClient>(`${baseUrl()}/clients/corporate`, client)
      .pipe(
        tap(() => {
          this.cachingService.clearCacheByTag(this.listCacheTag);
        }),
      );
  }

  public createIndividualClient(
    client: Partial<IClientsRequest>,
  ): Observable<IClient> {
    return this.http
      .post<IClient>(`${baseUrl()}/clients/individuals`, client)
      .pipe(
        tap(() => {
          this.cachingService.clearCacheByTag(this.listCacheTag);
        }),
      );
  }

  public clientLookup(
    params: ILookupRequest,
    noCache: boolean = false,
  ): Observable<ILookupResponse> {
    const HttpCatchingHeaders = noCache
      ? NgHttpCachingHeaders.DISALLOW_CACHE
      : NgHttpCachingHeaders.ALLOW_CACHE;

    return this.http.get<ILookupResponse>(`${baseUrl()}/clients/lookup`, {
      params: createQueryParams(params),
      headers: { [HttpCatchingHeaders]: '1' },
    });
  }

  public attachClient(
    params: Partial<IAssociateClientRequest>,
  ): Observable<IAssociateClientResponse> {
    return this.http
      .post<IAssociateClientResponse>(
        `${baseUrl()}/clients/attach-intermediary`,
        params,
      )
      .pipe(
        tap(() => {
          this.cachingService.clearCacheByTag(this.listCacheTag);
        }),
      );
  }

  public editIndividualClient(
    client: Partial<IndividualClientUpdateRequest>,
    id: number,
  ): Observable<IIndividualClient> {
    return this.http.put<IIndividualClient>(
      `${baseUrl()}/clients/${id}/individuals`,
      client,
    );
  }

  public getIndividualClient(id: number): Observable<IIndividualClient> {
    return this.http.get<IIndividualClient>(
      `${baseUrl()}/clients/${id}/individuals`,
    );
  }

  public editCorporateClient(
    client: Partial<ICorporateClientUpdateRequest>,
    id: number,
  ): Observable<ICorporateClient> {
    return this.http.put<ICorporateClient>(
      `${baseUrl()}/clients/${id}/corporate`,
      client,
    );
  }

  public getCorporateClient(id: number): Observable<ICorporateClient> {
    return this.http.get<ICorporateClient>(
      `${baseUrl()}/clients/${id}/corporate`,
    );
  }

  public editClientVehicle(
    id: number,
    request: IVehicleUpdateRequest,
  ): Observable<IVehicle> {
    return this.http.put<IVehicle>(
      `${baseUrl()}/clients/${id}/vehicle`,
      request,
    );
  }

  public getSingleClientsStats(
    id: number,
    params?: IClientStateQueryParams,
  ): Observable<IClientStats> {
    return this.http.get<IClientStats>(`${baseUrl()}/clients/${id}/stats`, {
      params: createQueryParams(params),
    });
  }

  getClientDocuments(id: number): Observable<IClientDocument[]> {
    return this.http.get<IClientDocument[]>(
      `${baseUrl()}/clients/${id}/documents`,
    );
  }

  UploadDocuments(
    id: number,
    data: IClientDocumentUploadRequest,
  ): Observable<IClientDocument> {
    return this.uploadService
      .bulkUploadDocuments({
        bucketName: 'inshuwa',
        files: data.files,
      })
      .pipe(
        switchMap((res) => {
          return this.http.post<IClientDocument>(
            `${baseUrl()}/clients/${id}/documents`,
            [
              {
                ...data,
                url: res.map((r) => r.url)[0],
              },
            ],
          );
        }),
      );
  }

  addBeneficiary(
    id: number,
    data: Partial<IBeneficiaryRequest>,
  ): Observable<IBeneficiary> {
    return this.http.post<IBeneficiary>(
      `${baseUrl()}/clients/${id}/beneficiaries`,
      data,
    );
  }

  updateBeneficiary(
    id: number,
    data: Partial<IBeneficiaryRequest>,
  ): Observable<IBeneficiary> {
    return this.http.put<IBeneficiary>(
      `${baseUrl()}/clients/beneficiaries/${id}`,
      data,
    );
  }

  getBeneficiaries(
    id: number,
    query?: { query: string },
  ): Observable<IBeneficiary[]> {
    return this.http.get<IBeneficiary[]>(
      `${baseUrl()}/clients/${id}/beneficiaries`,
      {
        params: createQueryParams(query),
      },
    );
  }

  getBeneficiariesPaginated(
    id: number,
    query?: { pageNumber: number; pageSize: number },
  ): Observable<IPaginatedResponse<IBeneficiary>> {
    return this.http.get<IPaginatedResponse<IBeneficiary>>(
      `${baseUrl()}/clients/${id}/beneficiaries/paginated`,
      {
        params: createQueryParams(query),
      },
    );
  }

  deleteBeneficiary(id: number): Observable<void> {
    return this.http.delete<void>(`${baseUrl()}/clients/beneficiaries/${id}`);
  }

  deleteClient(id: number): Observable<ISuccessulResponse> {
    return this.http.delete<ISuccessulResponse>(`${baseUrl()}/clients/${id}`);
  }

  public getClientById(id: string): Observable<IClient> {
    return this.http
      .get<IClient>(`${baseUrl()}/clients/${id}`)
      .pipe(map((x) => x));
  }

  static getLoggedInClient(): IClient {
    return JSON.parse(`${localStorage.getItem(SystemUserRole.client)}`);
  }

  isLoggedIn() {
    return localStorage.getItem(SystemUserRole.client) !== null;
  }

  saveClient(client: IClient) {
    localStorage.setItem(SystemUserRole.client, JSON.stringify(client));
  }

  clientGuard() {
    const { roles, clientId } = GetDecodedToken()!;
    return this.getClientById(clientId).pipe(
      map((value) => {
        if (!roles.includes(SystemUserRole.client)) {
          this.nzModalService.error({
            nzTitle: 'Unauthorized',
            nzContent: 'You are not authorized to access this page',
          });
          return false;
        }
        this.saveClient(value);
        this.isLoggedIn$.next(true);
        return true;
      }),
    );
  }

  deleteClientVehicle(vehicleId: number): Observable<ISuccessulResponse> {
    return this.http.delete<ISuccessulResponse>(
      `${baseUrl()}/clients/vehicle/${vehicleId}`,
    );
  }

  getClientStatements(
    id: number,
    params?: Partial<IClientStatementQueryParams>,
  ): Observable<IClientStatement[]> {
    return this.http.get<IClientStatement[]>(
      `${baseUrl()}/clients/${id}/report-listing/statements`,
      { params: createQueryParams(params) },
    );
  }

  downloadClientStatements(
    id: number,
    params?: Partial<IClientStatementQueryParams>,
  ) {
    return this.http.get(`${baseUrl()}/clients/${id}/documents/statements`, {
      responseType: 'blob',
      ...(params && { params }),
    });
  }

  downloadClientStatement(id: number, insurerId: number) {
    return this.http.get(
      `${baseUrl()}/clients/${id}/insurers/${insurerId}/documents/statements`,
      { responseType: 'blob' },
    );
  }
}
